import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
// Ensure Bootstrap Icons are installed: npm install bootstrap-icons
import "bootstrap-icons/font/bootstrap-icons.css";
import { AuthContext } from "../context/AuthContext";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear the specific error when the user starts typing again
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = "Email address is required.";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.password) {
      newErrors.password = "Password is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear any previous general errors

    try {
      // Calls the login function from AuthContext which hits your backend
      await login(formData);

      // On success, redirect the user to the dashboard
      navigate("/products");
    } catch (error) {
      // Safely extract the standardized error message from our backend
      const errorMessage =
        error.response?.data?.message ||
        "Invalid credentials. Please try again.";
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid vh-100 p-0">
      <div className="row g-0 h-100">
        {/* Left Panel: Brand Showcase (Hidden on Mobile) */}
        <div
          className="col-md-6 d-none d-md-flex flex-column justify-content-center align-items-center position-relative bg-dark"
          style={{
            backgroundImage: "url('/images/honeycomb.jpg')", // Add a nice honeycomb/luxury image here
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark Overlay for text readability */}
          <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-75"></div>

          <div className="position-relative z-1 text-center text-white p-5">
            <h1
              className="display-4 fw-bolder mb-3"
              style={{ letterSpacing: "4px" }}
            >
              BeeLuxe
            </h1>
          </div>
        </div>

        {/* Right Panel: Form Section */}
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-center bg-white">
          <div className="w-100 px-4 px-md-5" style={{ maxWidth: "500px" }}>
            {/* Mobile Header */}
            <div className="text-center d-md-none mb-4">
              <h2
                className="fw-bolder text-dark mb-1"
                style={{ letterSpacing: "2px" }}
              >
                BeeLuxe
              </h2>
            </div>

            <h3 className="fw-bold mb-1" style={{ color: "gold" }}>
              Login
            </h3>
            <p className="text-secondary mb-4">
              Please enter your credentials to sign in.
            </p>

            {/* Display Backend/Global Errors (like "Access denied") */}
            {errors.general && (
              <div className="alert alert-danger py-2 small" role="alert">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Floating Email Input */}
              <div className="form-floating mb-3">
                <input
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  id="email"
                  name="email"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                <label htmlFor="email" className="text-secondary">
                  Email address
                </label>
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>

              {/* Floating Password Input with Visibility Toggle */}
              <div className="form-floating mb-3 position-relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${errors.password ? "is-invalid" : ""}`}
                  id="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <label htmlFor="password" className="text-secondary">
                  Password
                </label>
                <button
                  type="button"
                  className="btn border-0 position-absolute top-50 end-0 translate-middle-y me-2 text-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  <i
                    className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                  ></i>
                </button>
                {errors.password && (
                  <div className="invalid-feedback">{errors.password}</div>
                )}
              </div>

              {/* Primary Submit Button */}
              <button
                type="submit"
                className="btn btn-dark w-100 py-3 mb-4 fw-bold shadow-sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Authenticating...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
